import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Image as ImageIcon, Search } from 'lucide-react';
import { api } from '../../lib/api';
import { Pagination } from '../../components/admin/Pagination';
import { useDebounce } from '../../hooks/useDebounce';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';

const empty = { name: '', description: '', cover_image: '', order: 0, featured: true };

function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

export default function AdminCollections() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [deleteId, setDeleteId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 9;
    const debouncedSearch = useDebounce(search);

    const load = async () => { const r = await api.get('/collections'); setItems(r.data); };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(
        () => items.filter((c) => !debouncedSearch || (c.name || '').toLowerCase().includes(debouncedSearch.toLowerCase()) || (c.description || '').toLowerCase().includes(debouncedSearch.toLowerCase())),
        [items, debouncedSearch]
    );
    useEffect(() => { setPage(1); }, [debouncedSearch]);
    const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
    const openEdit = (c) => { setEditing(c); setForm({ name: c.name, description: c.description, cover_image: c.cover_image, order: c.order, featured: c.featured }); setOpen(true); };

    const onImage = async (e) => {
        const file = e.target.files?.[0]; if (!file) return;
        const b64 = await fileToBase64(file); setForm((f) => ({ ...f, cover_image: b64 }));
    };

    const addImageUrl = () => { const url = window.prompt('Paste cover image URL'); if (url) setForm((f) => ({ ...f, cover_image: url })); };

    const onSave = async () => {
        setSaving(true);
        try {
            const payload = { ...form, order: Number(form.order) };
            if (editing) await api.put(`/admin/collections/${editing.id}`, payload);
            else await api.post('/admin/collections', payload);
            toast.success(editing ? 'Collection updated.' : 'Collection created.');
            setOpen(false); await load();
        } catch (err) { toast.error('Save failed'); } finally { setSaving(false); }
    };
    const onDelete = async () => {
        try { await api.delete(`/admin/collections/${deleteId}`); toast.success('Collection deleted.'); setDeleteId(null); await load(); }
        catch { toast.error('Delete failed'); }
    };

    return (
        <div data-testid="admin-collections-page">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <div>
                    <p className="eyebrow" style={{ color: 'var(--brand-burgundy)' }}>Atelier</p>
                    <h1 className="font-serif text-3xl sm:text-4xl mt-1" style={{ color: 'var(--brand-burgundy)' }}>Collections</h1>
                </div>
                <button onClick={openNew} data-testid="admin-collections-new-button" className="btn-burgundy px-5 py-2.5 rounded-xl text-xs tracking-[0.20em] uppercase inline-flex items-center gap-2 whitespace-nowrap">
                    <Plus className="w-4 h-4" /> New Collection
                </button>
            </div>

            <div className="mb-6 relative max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-muted)' }} />
                <input
                    data-testid="admin-collections-search-input"
                    type="text"
                    placeholder="Search collections..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="input-luxury pl-9"
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {paged.map((c) => (
                    <div key={c.id} className="luxury-card overflow-hidden" data-testid={`admin-collection-card-${c.slug}`}>
                        <div className="aspect-[16/10] bg-[var(--brand-cream)]">
                            {c.cover_image && <img src={c.cover_image} alt={c.name} className="w-full h-full object-cover" />}
                        </div>
                        <div className="p-4">
                            <h3 className="font-serif text-xl" style={{ color: 'var(--brand-burgundy)' }}>{c.name}</h3>
                            <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>{c.description}</p>
                            <div className="mt-3 flex justify-between items-center">
                                <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>Order: {c.order} · {c.featured ? 'Featured' : 'Hidden'}</span>
                                <div className="flex gap-1">
                                    <button onClick={() => openEdit(c)} data-testid={`admin-collection-edit-${c.slug}`} className="p-2 hover:bg-[var(--brand-rose)]/50 rounded"><Pencil className="w-4 h-4" style={{ color: 'var(--brand-burgundy)' }} /></button>
                                    <button onClick={() => setDeleteId(c.id)} className="p-2 hover:bg-[var(--brand-rose)]/50 rounded"><Trash2 className="w-4 h-4" style={{ color: 'var(--brand-burgundy)' }} /></button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {filtered.length === 0 && <p className="text-sm mt-4" style={{ color: 'var(--ink-muted)' }} data-testid="admin-collections-empty">No collections found.</p>}

            <Pagination page={page} pageSize={PAGE_SIZE} total={filtered.length} onPageChange={setPage} testIdPrefix="admin-collections-pagination" />

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg bg-white">
                    <DialogHeader><DialogTitle className="font-serif text-2xl" style={{ color: 'var(--brand-burgundy)' }}>{editing ? 'Edit Collection' : 'New Collection'}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div><label className="eyebrow block mb-1">Name</label><input className="input-luxury" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                        <div><label className="eyebrow block mb-1">Description</label><textarea rows={3} className="input-luxury resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                        <div>
                            <label className="eyebrow block mb-1">Cover Image</label>
                            {form.cover_image && <div className="mb-2 aspect-[16/10] rounded-lg overflow-hidden"><img src={form.cover_image} alt="cover" className="w-full h-full object-cover" /></div>}
                            <div className="flex gap-2">
                                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[rgba(90,36,50,0.18)] text-xs cursor-pointer"><ImageIcon className="w-4 h-4" /> Upload<input type="file" accept="image/*" className="hidden" onChange={onImage} /></label>
                                <button type="button" onClick={addImageUrl} className="px-3 py-2 rounded-lg border border-[rgba(90,36,50,0.18)] text-xs">Add by URL</button>
                                {form.cover_image && <button type="button" onClick={() => setForm({ ...form, cover_image: '' })} className="px-3 py-2 rounded-lg border border-[rgba(90,36,50,0.18)] text-xs">Clear</button>}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="eyebrow block mb-1">Order</label><input type="number" className="input-luxury" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></div>
                            <div className="flex items-end"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label></div>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                        <button onClick={() => setOpen(false)} className="px-4 py-2 text-xs uppercase tracking-[0.18em]">Cancel</button>
                        <button onClick={onSave} disabled={saving} className="btn-burgundy px-5 py-2.5 rounded-xl text-xs uppercase tracking-[0.18em] disabled:opacity-60">{saving ? 'Saving...' : 'Save'}</button>
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Delete collection?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={onDelete} className="btn-burgundy">Delete</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
